import { Checkbox, Input, InputNumber } from 'rsuite';
import React, { Dispatch, SetStateAction } from 'react';
import { FormField, FormGrid, FormSection } from '../../common/FormModal';
import LanguageTabs, {
  getLanguagePanelId,
  getLanguageTabId,
  LanguageKey,
} from '../../common/LanguageTabs';
import RichTextEditor from '../../common/RichTextEditor';

type Props = {
  modalTrainerSlug: string;
  modalTrainerName: string;
  modalTrainerNameEn: string;
  modalTrainerNameDe: string;
  modalTrainerTitle: string;
  modalTrainerTitleEn: string;
  modalTrainerTitleDe: string;
  modalTrainerDescription: string;
  modalTrainerDescriptionEn: string;
  modalTrainerDescriptionDe: string;
  modalTrainerShortDescription?: string;
  modalTrainerShortDescriptionEn?: string;
  modalTrainerShortDescriptionDe?: string;
  modalTrainerPriority?: number;
  modalTrainerActive: boolean;
  modalTrainerFeatured: boolean;
  setModalTrainerName:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerNameEn:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerNameDe:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerTitle:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerTitleEn:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerTitleDe:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerDescription:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerDescriptionEn:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerDescriptionDe:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerShortDescription?:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerShortDescriptionEn?:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerShortDescriptionDe?:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalTrainerPriority?:
    | Dispatch<SetStateAction<number>>
    | ((newValue: any) => void);
  setModalTrainerActive:
    | Dispatch<SetStateAction<boolean>>
    | ((newValue: any) => void);
  setModalTrainerFeatured:
    | Dispatch<SetStateAction<boolean>>
    | ((newValue: any) => void);
  setModalTrainerSlug:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
};

const SLUG_LIMIT = 80;
const NAME_LIMIT = 80;
const TITLE_LIMIT = 120;
const DESCRIPTION_LIMIT = 1200;
const SHORT_DESCRIPTION_LIMIT = 1000;

export default function TrainerModalContent({
  modalTrainerSlug,
  modalTrainerTitle,
  modalTrainerTitleEn,
  modalTrainerTitleDe,
  modalTrainerName,
  modalTrainerNameEn,
  modalTrainerNameDe,
  modalTrainerDescription,
  modalTrainerDescriptionEn,
  modalTrainerDescriptionDe,
  modalTrainerShortDescription,
  modalTrainerShortDescriptionEn,
  modalTrainerShortDescriptionDe,
  modalTrainerPriority,
  modalTrainerActive,
  modalTrainerFeatured,
  setModalTrainerTitle,
  setModalTrainerTitleEn,
  setModalTrainerTitleDe,
  setModalTrainerName,
  setModalTrainerNameEn,
  setModalTrainerNameDe,
  setModalTrainerDescription,
  setModalTrainerDescriptionEn,
  setModalTrainerDescriptionDe,
  setModalTrainerShortDescription,
  setModalTrainerShortDescriptionEn,
  setModalTrainerShortDescriptionDe,
  setModalTrainerPriority,
  setModalTrainerActive,
  setModalTrainerFeatured,
  setModalTrainerSlug,
}: Props) {
  const [activeLanguage, setActiveLanguage] = React.useState<LanguageKey>('ua');
  const languageTabPrefix = 'trainer-language';

  const languageConfig: Record<
    LanguageKey,
    {
      label: string;
      name: string;
      setName: (value: string) => void;
      title: string;
      setTitle: (value: string) => void;
      description: string;
      setDescription: (value: string) => void;
      shortDescription: string;
      setShortDescription: (value: string) => void;
    }
  > = {
    ua: {
      label: 'Українська',
      name: modalTrainerName,
      setName: setModalTrainerName,
      title: modalTrainerTitle,
      setTitle: setModalTrainerTitle,
      description: modalTrainerDescription,
      setDescription: setModalTrainerDescription,
      shortDescription: modalTrainerShortDescription || '',
      setShortDescription: setModalTrainerShortDescription || (() => {}),
    },
    en: {
      label: 'Англійська',
      name: modalTrainerNameEn,
      setName: setModalTrainerNameEn,
      title: modalTrainerTitleEn,
      setTitle: setModalTrainerTitleEn,
      description: modalTrainerDescriptionEn,
      setDescription: setModalTrainerDescriptionEn,
      shortDescription: modalTrainerShortDescriptionEn || '',
      setShortDescription: setModalTrainerShortDescriptionEn || (() => {}),
    },
    de: {
      label: 'Німецька',
      name: modalTrainerNameDe,
      setName: setModalTrainerNameDe,
      title: modalTrainerTitleDe,
      setTitle: setModalTrainerTitleDe,
      description: modalTrainerDescriptionDe,
      setDescription: setModalTrainerDescriptionDe,
      shortDescription: modalTrainerShortDescriptionDe || '',
      setShortDescription: setModalTrainerShortDescriptionDe || (() => {}),
    },
  };

  const activeLanguageConfig = languageConfig[activeLanguage];
  const slugValue = modalTrainerSlug || '';
  const nameValue = activeLanguageConfig.name || '';
  const titleValue = activeLanguageConfig.title || '';
  const descriptionValue = activeLanguageConfig.description || '';
  const shortDescriptionValue = activeLanguageConfig.shortDescription || '';
  const priorityValue = modalTrainerPriority ?? 0;

  return (
    <>
      <FormSection title="Статус">
        <div className="form-modal__toggles">
          <Checkbox
            checked={modalTrainerActive}
            onChange={(_value, checked) => {
              setModalTrainerActive(checked);
            }}
          >
            Відображати на сайті
          </Checkbox>
          <Checkbox
            checked={modalTrainerFeatured}
            onChange={(_value, checked) => {
              setModalTrainerFeatured(checked);
            }}
          >
            Виділяти тренера у списку
          </Checkbox>
          <span className="form-modal__hint">
            Виділені тренери мають синій фон на сайті.
          </span>
        </div>
      </FormSection>
      <FormSection title="Контент">
        <FormGrid>
          <FormField label="Slug" required maxLength={SLUG_LIMIT}>
            <Input
              placeholder="slug"
              value={slugValue}
              onChange={setModalTrainerSlug}
            />
          </FormField>
          <FormField label="Пріоритет">
            <InputNumber
              placeholder="0"
              value={priorityValue}
              onChange={value => {
                if (setModalTrainerPriority) {
                  setModalTrainerPriority(Number(value) || 0);
                }
              }}
              min={0}
            />
          </FormField>
        </FormGrid>
        <LanguageTabs
          activeLanguage={activeLanguage}
          onChange={setActiveLanguage}
          idPrefix={languageTabPrefix}
        />
        <div
          className="language-panel"
          role="tabpanel"
          id={getLanguagePanelId(languageTabPrefix, activeLanguage)}
          aria-labelledby={getLanguageTabId(languageTabPrefix, activeLanguage)}
        >
          <div className="language-fields">
            <FormGrid>
              <FormField label="Ім'я" maxLength={NAME_LIMIT}>
                <Input
                  placeholder="Ім'я"
                  value={nameValue}
                  onChange={activeLanguageConfig.setName}
                />
              </FormField>
              <FormField label="Заголовок" maxLength={TITLE_LIMIT}>
                <Input
                  placeholder="Заголовок"
                  value={titleValue}
                  onChange={activeLanguageConfig.setTitle}
                />
              </FormField>
            </FormGrid>
            <FormField
              label="Короткий опис"
              maxLength={SHORT_DESCRIPTION_LIMIT}
            >
              <Input
                as="textarea"
                rows={3}
                placeholder="Короткий опис тренера..."
                value={shortDescriptionValue}
                onChange={activeLanguageConfig.setShortDescription}
              />
            </FormField>
            <FormField label="Опис">
              <RichTextEditor
                value={descriptionValue}
                onChange={activeLanguageConfig.setDescription}
                placeholder="Опис тренера..."
              />
            </FormField>
          </div>
        </div>
      </FormSection>
    </>
  );
}
